import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './SensorDocumentation.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface SensorInstance {
  id: string;
  serial_number: string;
  asset_tag: string;
  sensor_name: string;
  sensor_type: string;
  unit: string;
  facility: string;
  subsystem: string;
  location_description: string;
  operational_status: string;
  health_status: string;
  digital_twin_reference: string;
  last_calibration_date: string;
  next_calibration_date: string;
  created_at: string;
  updated_at: string;
}

interface ChangeHistory {
  id: string;
  change_type: string;
  change_description: string;
  changed_fields: any;
  valid_from: string;
  change_reason: string;
  changed_by: string;
  created_at: string;
}

interface CalibrationRecord {
  id: string;
  calibration_date: string;
  calibration_type: string;
  calibration_standard: string;
  measured_offset: number;
  measured_gain: number;
  uncertainty: number;
  passed: boolean;
  certificate_number: string;
  calibrated_by: string;
  next_calibration_due: string;
  notes: string;
}

interface MaintenanceRecord {
  id: string;
  maintenance_date: string;
  maintenance_type: string;
  description: string;
  parts_replaced: string[];
  downtime_minutes: number;
  cost: number;
  performed_by: string;
  work_order_id: string;
  notes: string;
}

interface Document {
  id: string;
  document_type: string;
  title: string;
  file_url: string;
  file_type: string;
  version: string;
  description: string;
  uploaded_by: string;
  created_at: string;
}

const SensorDocumentation: React.FC = () => {
  const [instances, setInstances] = useState<SensorInstance[]>([]);
  const [selectedInstance, setSelectedInstance] = useState<string | null>(null);
  const [instanceDetails, setInstanceDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'calibration' | 'maintenance' | 'documents'>('overview');

  useEffect(() => {
    loadInstances();
  }, []);

  const loadInstances = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/sensor-instances`);
      setInstances(response.data);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Sensor-Instanzen');
    } finally {
      setLoading(false);
    }
  };

  const loadInstanceDetails = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/sensor-instances/${id}`);
      setInstanceDetails(response.data);
      setSelectedInstance(id);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#4caf50';
      case 'maintenance': return '#ff9800';
      case 'faulty': return '#f44336';
      case 'decommissioned': return '#9e9e9e';
      default: return '#2196f3';
    }
  };

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'good': return '#4caf50';
      case 'warning': return '#ff9800';
      case 'alarm': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const isCalibrationOverdue = (nextDate: string) => {
    if (!nextDate) return false;
    return new Date(nextDate) < new Date();
  };

  return (
    <div className="sensor-documentation">
      <div className="header">
        <h1>📋 Sensor-Dokumentation</h1>
        <p>Vollständige Dokumentation gemäß Anforderungskatalog für Digitale Zwillinge</p>
      </div>

      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      <div className="content-layout">
        {/* Liste der Sensor-Instanzen */}
        <div className="instances-list">
          <h2>Sensor-Instanzen</h2>
          {loading && !selectedInstance && <div className="loading">Lädt...</div>}
          
          <div className="instances-grid">
            {instances.map((instance) => (
              <div
                key={instance.id}
                className={`instance-card ${selectedInstance === instance.id ? 'selected' : ''}`}
                onClick={() => loadInstanceDetails(instance.id)}
              >
                <div className="instance-header">
                  <h3>{instance.sensor_name}</h3>
                  <div className="status-badges">
                    <span 
                      className="status-badge" 
                      style={{ backgroundColor: getStatusColor(instance.operational_status) }}
                    >
                      {instance.operational_status}
                    </span>
                    <span 
                      className="health-badge" 
                      style={{ backgroundColor: getHealthColor(instance.health_status) }}
                    >
                      {instance.health_status}
                    </span>
                  </div>
                </div>
                
                <div className="instance-info">
                  <div className="info-row">
                    <span className="label">Seriennummer:</span>
                    <span className="value">{instance.serial_number || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Asset-Tag:</span>
                    <span className="value">{instance.asset_tag || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Typ:</span>
                    <span className="value">{instance.sensor_type}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Standort:</span>
                    <span className="value">{instance.location_description || 'N/A'}</span>
                  </div>
                  {instance.next_calibration_date && (
                    <div className={`info-row ${isCalibrationOverdue(instance.next_calibration_date) ? 'overdue' : ''}`}>
                      <span className="label">Nächste Kalibrierung:</span>
                      <span className="value">{formatDate(instance.next_calibration_date)}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Details der ausgewählten Instanz */}
        {selectedInstance && instanceDetails && (
          <div className="instance-details">
            <div className="details-header">
              <h2>{instanceDetails.instance.sensor_name}</h2>
              <button onClick={() => setSelectedInstance(null)} className="close-button">✕</button>
            </div>

            <div className="tabs">
              <button 
                className={activeTab === 'overview' ? 'active' : ''}
                onClick={() => setActiveTab('overview')}
              >
                Übersicht
              </button>
              <button 
                className={activeTab === 'history' ? 'active' : ''}
                onClick={() => setActiveTab('history')}
              >
                Änderungshistorie ({instanceDetails.changeHistory?.length || 0})
              </button>
              <button 
                className={activeTab === 'calibration' ? 'active' : ''}
                onClick={() => setActiveTab('calibration')}
              >
                Kalibrierung ({instanceDetails.calibrationRecords?.length || 0})
              </button>
              <button 
                className={activeTab === 'maintenance' ? 'active' : ''}
                onClick={() => setActiveTab('maintenance')}
              >
                Wartung ({instanceDetails.maintenanceRecords?.length || 0})
              </button>
              <button 
                className={activeTab === 'documents' ? 'active' : ''}
                onClick={() => setActiveTab('documents')}
              >
                Dokumente ({instanceDetails.documents?.length || 0})
              </button>
            </div>

            <div className="tab-content">
              {activeTab === 'overview' && (
                <div className="overview-tab">
                  <section className="detail-section">
                    <h3>🔍 Identität</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="label">Seriennummer:</span>
                        <span className="value">{instanceDetails.instance.serial_number || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Asset-Tag:</span>
                        <span className="value">{instanceDetails.instance.asset_tag || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Firmware:</span>
                        <span className="value">{instanceDetails.instance.firmware_version || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Hardware-Revision:</span>
                        <span className="value">{instanceDetails.instance.hardware_revision || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Installationsdatum:</span>
                        <span className="value">{formatDate(instanceDetails.instance.installation_date)}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Hersteller:</span>
                        <span className="value">{instanceDetails.instance.manufacturer_name || 'N/A'}</span>
                      </div>
                    </div>
                  </section>

                  <section className="detail-section">
                    <h3>📍 Standort & Einbaulage</h3>
                    <div className="detail-grid">
                      <div className="detail-item full-width">
                        <span className="label">Standortbeschreibung:</span>
                        <span className="value">{instanceDetails.instance.location_description || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Montageposition:</span>
                        <span className="value">{instanceDetails.instance.mounting_position || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Subsystem:</span>
                        <span className="value">{instanceDetails.instance.subsystem || 'N/A'}</span>
                      </div>
                    </div>
                  </section>

                  <section className="detail-section">
                    <h3>📊 Mess-/Stellgrößen</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="label">Physikalische Größe:</span>
                        <span className="value">{instanceDetails.instance.sensor_type}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Einheit:</span>
                        <span className="value">{instanceDetails.instance.unit}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Genauigkeit:</span>
                        <span className="value">
                          {instanceDetails.instance.accuracy ? `±${instanceDetails.instance.accuracy} ${instanceDetails.instance.accuracy_unit || ''}` : 'N/A'}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Auflösung:</span>
                        <span className="value">{instanceDetails.instance.resolution || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Ansprechzeit:</span>
                        <span className="value">{instanceDetails.instance.response_time_ms ? `${instanceDetails.instance.response_time_ms} ms` : 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Toleranz (oben/unten):</span>
                        <span className="value">
                          {instanceDetails.instance.tolerance_upper && instanceDetails.instance.tolerance_lower 
                            ? `${instanceDetails.instance.tolerance_upper} / ${instanceDetails.instance.tolerance_lower}`
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </section>

                  <section className="detail-section">
                    <h3>⚠️ Grenzwerte</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="label">Warnung (oben):</span>
                        <span className="value warning">{instanceDetails.instance.threshold_warning_upper || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Warnung (unten):</span>
                        <span className="value warning">{instanceDetails.instance.threshold_warning_lower || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Alarm (oben):</span>
                        <span className="value alarm">{instanceDetails.instance.threshold_alarm_upper || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Alarm (unten):</span>
                        <span className="value alarm">{instanceDetails.instance.threshold_alarm_lower || 'N/A'}</span>
                      </div>
                    </div>
                  </section>

                  <section className="detail-section">
                    <h3>🔧 Betriebsdaten</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="label">Kalibrierintervall:</span>
                        <span className="value">{instanceDetails.instance.calibration_interval_days ? `${instanceDetails.instance.calibration_interval_days} Tage` : 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Letzte Kalibrierung:</span>
                        <span className="value">{formatDate(instanceDetails.instance.last_calibration_date)}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Nächste Kalibrierung:</span>
                        <span className={`value ${isCalibrationOverdue(instanceDetails.instance.next_calibration_date) ? 'overdue' : ''}`}>
                          {formatDate(instanceDetails.instance.next_calibration_date)}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Erwartete Lebensdauer:</span>
                        <span className="value">{instanceDetails.instance.expected_lifetime_hours ? `${instanceDetails.instance.expected_lifetime_hours} h` : 'N/A'}</span>
                      </div>
                    </div>
                  </section>

                  <section className="detail-section">
                    <h3>🌐 Kommunikationsdaten</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="label">Protokoll:</span>
                        <span className="value">{instanceDetails.instance.protocol || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Netzwerkadresse:</span>
                        <span className="value">{instanceDetails.instance.network_address || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Geräte-ID:</span>
                        <span className="value">{instanceDetails.instance.device_id || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Update-Rate:</span>
                        <span className="value">{instanceDetails.instance.update_rate_ms ? `${instanceDetails.instance.update_rate_ms} ms` : 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Zeitstempel-Qualität:</span>
                        <span className="value">{instanceDetails.instance.timestamp_quality || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Sicherheitslevel:</span>
                        <span className="value">{instanceDetails.instance.security_level || 'N/A'}</span>
                      </div>
                    </div>
                  </section>

                  <section className="detail-section">
                    <h3>🔗 Digitaler Zwilling Mapping</h3>
                    <div className="detail-grid">
                      <div className="detail-item full-width">
                        <span className="label">DT-Referenz:</span>
                        <span className="value code">{instanceDetails.instance.digital_twin_reference || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Tag-Name:</span>
                        <span className="value code">{instanceDetails.instance.tag_name || 'N/A'}</span>
                      </div>
                      <div className="detail-item full-width">
                        <span className="label">Signal-Kontext:</span>
                        <span className="value">{instanceDetails.instance.signal_context || 'N/A'}</span>
                      </div>
                      <div className="detail-item full-width">
                        <span className="label">Funktionsbezug:</span>
                        <span className="value">{instanceDetails.instance.functional_purpose || 'N/A'}</span>
                      </div>
                    </div>
                  </section>

                  {instanceDetails.instance.notes && (
                    <section className="detail-section">
                      <h3>📝 Notizen</h3>
                      <p className="notes">{instanceDetails.instance.notes}</p>
                    </section>
                  )}
                </div>
              )}

              {activeTab === 'history' && (
                <div className="history-tab">
                  <h3>📜 Änderungshistorie</h3>
                  {instanceDetails.changeHistory && instanceDetails.changeHistory.length > 0 ? (
                    <div className="history-list">
                      {instanceDetails.changeHistory.map((change: ChangeHistory) => (
                        <div key={change.id} className="history-item">
                          <div className="history-header">
                            <span className="change-type">{change.change_type}</span>
                            <span className="change-date">{formatDate(change.valid_from)}</span>
                          </div>
                          <p className="change-description">{change.change_description}</p>
                          {change.change_reason && (
                            <p className="change-reason"><strong>Grund:</strong> {change.change_reason}</p>
                          )}
                          {change.changed_by && (
                            <p className="changed-by"><strong>Geändert von:</strong> {change.changed_by}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-data">Keine Änderungshistorie vorhanden</p>
                  )}
                </div>
              )}

              {activeTab === 'calibration' && (
                <div className="calibration-tab">
                  <h3>🎯 Kalibrierungshistorie</h3>
                  {instanceDetails.calibrationRecords && instanceDetails.calibrationRecords.length > 0 ? (
                    <div className="calibration-list">
                      {instanceDetails.calibrationRecords.map((cal: CalibrationRecord) => (
                        <div key={cal.id} className="calibration-item">
                          <div className="calibration-header">
                            <span className={`calibration-status ${cal.passed ? 'passed' : 'failed'}`}>
                              {cal.passed ? '✓ Bestanden' : '✗ Nicht bestanden'}
                            </span>
                            <span className="calibration-date">{formatDate(cal.calibration_date)}</span>
                          </div>
                          <div className="calibration-details">
                            <p><strong>Typ:</strong> {cal.calibration_type}</p>
                            <p><strong>Standard:</strong> {cal.calibration_standard || 'N/A'}</p>
                            {cal.measured_offset !== null && <p><strong>Offset:</strong> {cal.measured_offset}</p>}
                            {cal.measured_gain !== null && <p><strong>Verstärkung:</strong> {cal.measured_gain}</p>}
                            {cal.uncertainty !== null && <p><strong>Unsicherheit:</strong> {cal.uncertainty}</p>}
                            {cal.certificate_number && <p><strong>Zertifikat:</strong> {cal.certificate_number}</p>}
                            {cal.calibrated_by && <p><strong>Kalibriert von:</strong> {cal.calibrated_by}</p>}
                            <p><strong>Nächste Kalibrierung:</strong> {formatDate(cal.next_calibration_due)}</p>
                            {cal.notes && <p className="notes">{cal.notes}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-data">Keine Kalibrierungshistorie vorhanden</p>
                  )}
                </div>
              )}

              {activeTab === 'maintenance' && (
                <div className="maintenance-tab">
                  <h3>🔧 Wartungshistorie</h3>
                  {instanceDetails.maintenanceRecords && instanceDetails.maintenanceRecords.length > 0 ? (
                    <div className="maintenance-list">
                      {instanceDetails.maintenanceRecords.map((maint: MaintenanceRecord) => (
                        <div key={maint.id} className="maintenance-item">
                          <div className="maintenance-header">
                            <span className="maintenance-type">{maint.maintenance_type}</span>
                            <span className="maintenance-date">{formatDate(maint.maintenance_date)}</span>
                          </div>
                          <p className="maintenance-description">{maint.description}</p>
                          {maint.parts_replaced && maint.parts_replaced.length > 0 && (
                            <p><strong>Ersetzte Teile:</strong> {maint.parts_replaced.join(', ')}</p>
                          )}
                          {maint.downtime_minutes && <p><strong>Ausfallzeit:</strong> {maint.downtime_minutes} Minuten</p>}
                          {maint.cost && <p><strong>Kosten:</strong> {maint.cost.toFixed(2)} €</p>}
                          {maint.performed_by && <p><strong>Durchgeführt von:</strong> {maint.performed_by}</p>}
                          {maint.work_order_id && <p><strong>Arbeitsauftrag:</strong> {maint.work_order_id}</p>}
                          {maint.notes && <p className="notes">{maint.notes}</p>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-data">Keine Wartungshistorie vorhanden</p>
                  )}
                </div>
              )}

              {activeTab === 'documents' && (
                <div className="documents-tab">
                  <h3>📄 Dokumente</h3>
                  {instanceDetails.documents && instanceDetails.documents.length > 0 ? (
                    <div className="documents-list">
                      {instanceDetails.documents.map((doc: Document) => (
                        <div key={doc.id} className="document-item">
                          <div className="document-icon">📄</div>
                          <div className="document-info">
                            <h4>{doc.title}</h4>
                            <p className="document-type">{doc.document_type}</p>
                            {doc.description && <p className="document-description">{doc.description}</p>}
                            <div className="document-meta">
                              {doc.version && <span>Version: {doc.version}</span>}
                              {doc.uploaded_by && <span>Hochgeladen von: {doc.uploaded_by}</span>}
                              <span>{formatDate(doc.created_at)}</span>
                            </div>
                          </div>
                          {doc.file_url && (
                            <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="download-button">
                              ⬇️ Download
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-data">Keine Dokumente vorhanden</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SensorDocumentation;
